import type { ColumnFiltersState, OnChangeFn, RowData, SortingState } from "@tanstack/react-table";
import type { ColumnDef, ColumnMeta } from "./features";
import type { ColumnLessFilter } from "./types";

// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
export function getColumnId(column: ColumnDef<any, any>) {
  const rawId = "accessorKey" in column ? column.accessorKey : (column.id ?? "");
  const transformedId = typeof rawId === "string" ? rawId.replaceAll(".", "_") : rawId;
  return transformedId;
}

/**
 * Recurses to arbitrary depth, matching TanStack's own `getFlatColumns`, which walks `column.columns` all the
 * way down. Stopping at one level would leave leaves of a nested group out of `assertUniqueColumnIds` while
 * TanStack's by-id lookup still sees them.
 */
function flattenColumns<TData extends RowData>(
  // `readonly` because that is how TanStack types a group's nested `columns`, which the recursion passes back in
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  columns: ReadonlyArray<ColumnDef<TData, any>>,
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
): Array<ColumnDef<TData, any>> {
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  return columns.flatMap<ColumnDef<TData, any>>((column) =>
    "columns" in column && column.columns ? [column, ...flattenColumns(column.columns)] : [column],
  );
}

/**
 * Two defs resolving to the same id silently break the table: TanStack's flat-column lookup keeps the last
 * one, so when that def has no accessor every cell in the visible column renders `renderFallbackValue`
 * ("N/A"). `table.getColumn(id)` still finds *a* column, so TanStack never warns. Throwing turns the mistake
 * into a red build, since Storybook renders every one of these tables in CI.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
export function assertUniqueColumnIds<TData extends RowData>(columns: Array<ColumnDef<TData, any>>) {
  const seen = new Set<string>();
  for (const column of flattenColumns(columns)) {
    const columnId = String(getColumnId(column));
    if (!columnId) continue;
    if (seen.has(columnId)) {
      throw new Error(`Duplicate column id "${columnId}" — two column defs resolve to the same id`);
    }
    seen.add(columnId);
  }
}

// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
type FilterVariant = NonNullable<ColumnMeta<any, any>["filter"]>["variant"];
type FilterKeyMapping = Record<string, { mappedKey: string; type: FilterVariant }>;

export function useFilters<TData extends RowData, TValue>(options: {
  columns: Array<ColumnDef<TData, TValue>>;
  columnFilters: ColumnFiltersState | undefined;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState> | undefined;
}) {
  "use memo";
  const { columns, columnFilters, onColumnFiltersChange: onChange } = options;

  const [filterToColumnLookup, columnToFilterLookup] = getFilterLookups(columns, !!onChange);

  const mappedColumnFilters = mapFilters(columnFilters ?? [], filterToColumnLookup);

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) =>
    onChange?.((old) => {
      const columnFilters = typeof updater === "function" ? updater(mapFilters(old, filterToColumnLookup)) : updater;
      return mapFilters(columnFilters, columnToFilterLookup).filter((filter) => !!filter.id);
    });
  if (!columnFilters || !onChange) return {};
  return { columnFilters: mappedColumnFilters, onColumnFiltersChange };
}

function getFilterLookups<TData extends RowData, TValue>(
  columns: Array<ColumnDef<TData, TValue>>,
  hasChangeHandler: boolean,
): [FilterKeyMapping, FilterKeyMapping] {
  if (!hasChangeHandler) return [{}, {}];
  const filterToColumn: FilterKeyMapping = {};
  const columnToFilter: FilterKeyMapping = {};
  for (const column of flattenColumns(columns)) {
    // need to compare to `false` because `undefined` means default, which is `true`
    if (column.enableColumnFilter === false) continue;
    const columnKey = String(getColumnId(column));
    const filterKey = column.meta?.filter?.key;
    if (columnKey && !filterKey) throw new Error(`Column ${columnKey} is missing a filter key`);
    const type = column.meta?.filter?.variant ?? "text";
    if (filterKey && columnKey) {
      columnToFilter[columnKey] = { mappedKey: filterKey, type };
      filterToColumn[filterKey] = { mappedKey: columnKey, type };
    }
  }
  return [filterToColumn, columnToFilter];
}

function mapFilters(filters: ColumnFiltersState, mapping: FilterKeyMapping): ColumnFiltersState {
  return filters
    .map(({ id, value }) => {
      const mapped = mapping[id];
      if (!mapped) return { id: "", value: "" };
      if (!value) return { id: mapped.mappedKey, value };
      let transformedValue = value;
      if (mapped.type === "date") {
        // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
        transformedValue = new Date(value as any);
      } else if (mapped.type === "number") {
        transformedValue = Number(value);
      }
      return { id: mapped.mappedKey, value: transformedValue };
    })
    .filter(({ id }) => !!id);
}

// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
export const filterColumnHelper = <TData extends RowData>(filter: ColumnLessFilter): ColumnDef<TData, any> => ({
  id: filter.key,
  header: filter.name,
  meta: { filter: { ...filter, hideColumn: true } },
});

type SortingKeyMapping = Record<string, string>;

export function useSorting<TData extends RowData, TValue>(options: {
  columns: Array<ColumnDef<TData, TValue>>;
  sorting: SortingState | undefined;
  onSortingChange: OnChangeFn<SortingState> | undefined;
}) {
  "use memo";
  const { columns, sorting, onSortingChange: onChange } = options;

  const [sortToColumnLookup, columnToSortLookup] = getSortingLookups(columns, !!onChange);

  const mappedSorting = mapSorting(sorting ?? [], sortToColumnLookup);
  const onSortingChange: OnChangeFn<SortingState> = (updater) =>
    onChange?.((old) => {
      const sorting = typeof updater === "function" ? updater(mapSorting(old, sortToColumnLookup)) : updater;
      return mapSorting(sorting, columnToSortLookup).filter((sort) => !!sort.id);
    });
  if (!sorting || !onChange) return {};
  return { sorting: mappedSorting, onSortingChange };
}

function getSortingLookups<TData extends RowData, TValue>(
  columns: Array<ColumnDef<TData, TValue>>,
  hasChangeHandler: boolean,
): [SortingKeyMapping, SortingKeyMapping] {
  if (!hasChangeHandler) return [{}, {}];
  const sortToColumn: SortingKeyMapping = {};
  const columnToSort: SortingKeyMapping = {};
  for (const column of flattenColumns(columns)) {
    // need to compare to `false` because `undefined` means default, which is `true`
    if (column.enableSorting === false) continue;
    const columnKey = String(getColumnId(column));
    const sortKey = column.meta?.sortKey ?? columnKey;
    if (sortKey && columnKey) {
      columnToSort[columnKey] = sortKey;
      sortToColumn[sortKey] = columnKey;
    }
  }
  return [sortToColumn, columnToSort];
}

function mapSorting(sorting: SortingState, mapping: SortingKeyMapping): SortingState {
  return sorting
    .map(({ id, desc }) => {
      const mapped = mapping[id];
      if (!mapped) return { id: "", desc: false };
      return { id: mapped, desc };
    })
    .filter(({ id }) => !!id);
}
